import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Screenshots are lazy-loaded without fixed dimensions and the web font can swap in late, so the
// content above the target can change height after the jump. Until the reader interacts, re-align
// for this long.
const SETTLE_DURATION_MS = 2500;
const USER_INTERACTION_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];
const getIdFromHash = (hash) => {
    const rawId = hash.replace(/^#/, '');
    try {
        return decodeURIComponent(rawId);
    }
    catch {
        return rawId; // malformed escape sequence
    }
};
const isPlainLeftClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
/** Jumps to `target` (offset via CSS scroll-margin-top) and returns a cleanup function. */
const scrollToTarget = (target, container) => {
    const align = () => target.scrollIntoView({ block: 'start', behavior: 'instant' });
    let settleTimeout;
    const resizeObserver = new ResizeObserver(align);
    const stopSettling = () => {
        window.clearTimeout(settleTimeout);
        resizeObserver.disconnect();
        // `load` doesn't bubble, hence the capture phase.
        container.removeEventListener('load', align, true);
        USER_INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, stopSettling));
    };
    align();
    // Like a native fragment jump: keyboard and screen reader users continue from the target.
    target.focus({ preventScroll: true });
    container.addEventListener('load', align, true);
    resizeObserver.observe(container);
    USER_INTERACTION_EVENTS.forEach((type) => window.addEventListener(type, stopSettling, { passive: true }));
    settleTimeout = window.setTimeout(stopSettling, SETTLE_DURATION_MS);
    return stopSettling;
};
/**
 * Same-page navigation for the rendered manual inside `containerRef`:
 * - `#id` links (TOC, back links, links inside the imported HTML) go through the router, then
 *   the page scrolls to the target. This also covers opening the page with a hash and back/forward.
 * - Clicking an image that isn't a link opens it in full size in a new tab.
 */
export const useManualNavigation = (containerRef, isReady) => {
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        const container = containerRef.current;
        if (!isReady || !container) {
            return undefined;
        }
        const handleClick = (event) => {
            if (event.defaultPrevented ||
                !isPlainLeftClick(event) ||
                !(event.target instanceof Element)) {
                return;
            }
            const link = event.target.closest('a');
            if (link) {
                const href = link.getAttribute('href') ?? '';
                if (href.length > 1 && href.startsWith('#')) {
                    event.preventDefault();
                    navigate({ pathname: location.pathname, search: location.search, hash: href }, 
                    // Clicking the current anchor again only scrolls, without a new history entry.
                    { replace: href === location.hash });
                }
                return;
            }
            const image = event.target.closest('.creator-manual-html img');
            if (image instanceof HTMLImageElement) {
                window.open(image.currentSrc || image.src, '_blank', 'noopener,noreferrer');
            }
        };
        container.addEventListener('click', handleClick);
        return () => container.removeEventListener('click', handleClick);
    }, [containerRef, isReady, location.pathname, location.search, location.hash, navigate]);
    // location.key changes on every navigation, so clicking the same anchor again re-scrolls.
    useEffect(() => {
        const container = containerRef.current;
        const targetId = getIdFromHash(location.hash);
        if (!isReady || !container || !targetId) {
            return undefined;
        }
        const target = document.getElementById(targetId);
        return target ? scrollToTarget(target, container) : undefined;
    }, [containerRef, isReady, location.hash, location.key]);
};
